import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '@/components/base/Button';
// Creator Discovery is still on mock data, so its profiles (e.g. crt-zawadi) come from here.
import { creators } from '@/mocks/creators';
import type { Creator, CreatorProfile } from '@/types/portal';
import { api, ApiError } from '@/utils/api';
import { useRole } from '@/context/RoleContext';
import ProfileHeader from './components/ProfileHeader';
import AiAssessmentPanel from './components/AiAssessmentPanel';
import AiFitPanel from './components/AiFitPanel';
import PlatformPanel from './components/PlatformPanel';
import AudiencePanel from './components/AudiencePanel';
import VettingPanel from './components/VettingPanel';
import CampaignHistoryPanel from './components/CampaignHistoryPanel';

export default function CreatorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useRole();
  // Sample Discovery creators have ids like crt-zawadi; every other id is a real
  // creator (user id, or the old crt-12) and loads from the API.
  const mockCreator: Creator | undefined = creators.find((entry) => entry.id === id);
  const fromApi = Boolean(id) && !mockCreator;
  // Discovery passes the creator it showed: on the agency API, which has no profile
  // endpoint, that summary is shown instead of "not found".
  const passedCreator = (location.state as { creator?: CreatorProfile } | null)?.creator;
  const [summaryOnly, setSummaryOnly] = useState(false);
  const [apiCreator, setApiCreator] = useState<CreatorProfile | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const creator: CreatorProfile | undefined = mockCreator ?? apiCreator ?? undefined;
  // The AI assessment is a pre-contract vetting step for creators found via
  // Discovery — it is not shown when a profile is opened mid-campaign.
  const fromDiscovery =
    (location.state as { from?: string } | null)?.from === 'discovery';
  const [shortlisted, setShortlisted] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!fromApi) return;
    let cancelled = false;
    setLoadState('loading');
    setApiCreator(null);
    setSummaryOnly(false);
    api<CreatorProfile>(`/creators/${id}`)
      .then((profile) => {
        if (cancelled) return;
        setApiCreator(profile);
        setLoadState('ready');
      })
      .catch((error: ApiError) => {
        if (cancelled) return;
        if (error.status === 404) {
          if (passedCreator?.id === id) {
            setApiCreator(passedCreator);
            setSummaryOnly(true);
          }
          setLoadState('ready');
        } else {
          setLoadError(error.message);
          setLoadState('error');
        }
      });
    return () => {
      cancelled = true;
    };
    // passedCreator is read once per id; it isn't a reason to load again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromApi, id]);

  if (fromApi && loadState === 'loading') {
    return (
      <div className="flex items-center justify-center px-4 py-24 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin" />
        Loading creator…
      </div>
    );
  }

  if (fromApi && loadState === 'error') {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 py-12 text-center">
          <p className="text-sm text-primary-800">
            <i className="ri-error-warning-line mr-1" />
            {loadError}
          </p>
          <Button variant="outline" size="sm" icon="ri-arrow-left-line" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
            <i className="ri-user-search-line" />
          </span>
          <h1 className="mt-3 font-heading text-lg font-semibold text-foreground-950">
            Creator not found
          </h1>
          <p className="mt-1 max-w-sm text-sm text-foreground-600">
            This profile may have been removed or the link is out of date.
          </p>
          <div className="mt-4">
            <Button variant="outline" icon="ri-arrow-left-line" onClick={() => navigate('/discovery')}>
              Back to discovery
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/discovery" className="cursor-pointer hover:text-foreground-800">
          Creator Discovery
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{creator.name}</span>
      </nav>

      <div className="mt-4">
        <ProfileHeader
          creator={creator}
          shortlisted={shortlisted}
          onShortlist={() => {
            setShortlisted((prev) => !prev);
            setNotice(shortlisted ? 'Removed from shortlist.' : 'Added to shortlist.');
          }}
          onInvite={() => setNotice(`Invitation drafted for ${creator.name}.`)}
          onReject={() => setNotice('Marked as not a fit. It will be excluded from recommendations.')}
        />
      </div>

      {summaryOnly ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2.5 text-sm text-secondary-900">
          <i className="ri-information-line mt-0.5" />
          Showing Discovery's summary and their campaigns with you: the production API has no creator profile
          yet, so connected platforms and audience aren't available here.
        </p>
      ) : null}

      {notice ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="text-accent-700 cursor-pointer hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          {/* The AI assessment still reads mock vetting data, so it only shows for mock creators. */}
          {fromDiscovery && mockCreator ? <AiAssessmentPanel creator={mockCreator} /> : null}
          <AiFitPanel creator={creator} />
          {summaryOnly ? null : <PlatformPanel creator={creator} />}
          {fromApi && can('campaigns.view') ? <CampaignHistoryPanel creatorId={creator.id} /> : null}
          {summaryOnly ? null : <AudiencePanel creator={creator} />}
        </div>
        <div className="lg:sticky lg:top-20 lg:h-fit">
          <VettingPanel creator={creator} />
        </div>
      </div>
    </div>
  );
}